import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";

import { CoachReportSummary } from "../../../../../api/services/reports/models";
import { ReactComponent as ExportIcon } from "../../../../../assets/images/export.svg";
import {
  Column,
  FormController,
  MuiBox,
  MuiButtonTertiary,
  MuiTable,
  MuiTextFieldWithSearchIcon,
  MuiTypography,
  RowProps,
} from "../../../../../components";
import MuiEllipseLabel from "../../../../../components/ellipseLabel/MuiEllipseLabel";
import { MAX_LENGTH_50, PAGE_IDS } from "../../../../../helpers/constants";
import { filterListByFilterKeyValue } from "../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../../redux/slices/appState/appStateSelector";
import { mapCoachReports } from "../../../helper";
import {
  CoachReportKey,
  CoachReportSummaryRow,
} from "../../../modelsAndSchemas/models";
import { reportsResultsSiteCoreKeys } from "../../../siteCore/SiteCoreKeys";

const pageId = `${PAGE_IDS.REPORTS}-list`;

const {
  assignedCoach,
  activeAssignments,
  openAssignments,
  inactiveAssignments,
} = CoachReportKey;

interface CoachReportResultProps {
  coachReports: CoachReportSummary[];
  isFilteredCoachReport: boolean;
  onExport: () => void;
}
export const CoachReportResult = ({
  coachReports,
  isFilteredCoachReport = false,
  onExport,
}: CoachReportResultProps) => {
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);

  const { commonLabels, useFetchLabel } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const [filteredReports, setFilteredReports] = useState<
    CoachReportSummaryRow[]
  >([]);

  const { control, reset } = useForm({
    defaultValues: {
      [assignedCoach]: "",
      [activeAssignments]: "",
      [openAssignments]: "",
      [inactiveAssignments]: "",
    },
  });

  //   watch entire form state
  const filters = useWatch({ control });

  const getLabel = useFetchLabel(reportsResultsSiteCoreKeys.header);

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  const formattedCoachReport = useMemo(() => {
    reset();
    setFilteredReports([]);
    if (!coachReports?.length) return [];
    return mapCoachReports(coachReports);
  }, [coachReports, siteCoreLoading]);

  const appliedFilters = () => {
    if (!formattedCoachReport.length) return;
    const filterList = filterListByFilterKeyValue(
      formattedCoachReport,
      filters,
    ) as CoachReportSummaryRow[];
    setFilteredReports(filterList);
    resetPagination();
  };

  useEffect(() => {
    appliedFilters();
  }, [filters]);

  const tableRef = useRef<{ resetPagination: () => void }>(null);

  const renderLabel = (value: string) => {
    return (
      <MuiBox>
        <MuiEllipseLabel
          labelTypoClassName="coach-name-label"
          text={value}
          labelTypoVariant="h3"
          placement="bottom-start"
        />
      </MuiBox>
    );
  };

  const columns: Column[] = [
    {
      id: assignedCoach,
      label: getLabel(reportsResultsSiteCoreKeys.assignedCoach),
      isSortVisible: true,
      render: (row: RowProps) => renderLabel(row[assignedCoach]),
    },
    {
      id: activeAssignments,
      label: getLabel(reportsResultsSiteCoreKeys.activeAssignments),
      isSortVisible: true,
    },
    {
      id: openAssignments,
      label: getLabel(reportsResultsSiteCoreKeys.openAssignments),
      isSortVisible: true,
    },
    {
      id: inactiveAssignments,
      label: getLabel(reportsResultsSiteCoreKeys.inactiveAssignments),
      isSortVisible: true,
    },
  ];

  const renderFilterTextField = (name: string) => {
    return (
      <FormController key={name} control={control} name={name}>
        <MuiTextFieldWithSearchIcon
          key={name}
          id={`${pageId}-${name}`}
          aria-label={commonLabels.search}
          searchARIALabel={commonLabels.search}
          data-testid={`${name}-search-field`}
          showCloseIcon={true}
          placeholder={commonLabels.search}
          maxLength={MAX_LENGTH_50}
          requiredARIALabel={commonARIALabel.required}
          shouldTrimSpaces={false}
        />
      </FormController>
    );
  };
  const tableCoachReportHeaderForm: React.JSX.Element[] = [
    renderFilterTextField(assignedCoach),
    <></>,
    <></>,
    <></>,
  ];

  return (
    <MuiBox className="result-container">
      <MuiBox className="result-header">
        <MuiTypography variant="h2SemiBold" className="header-text">
          {commonLabels.allRecords}
        </MuiTypography>
        {coachReports.length > 0 && (
          <MuiTypography variant="h3SemiBold" className="header-count">
            {coachReports.length}
          </MuiTypography>
        )}
        <MuiBox textAlign="right" flexGrow={1}>
          <MuiButtonTertiary data-testid="export-button" onClick={onExport}>
            <ExportIcon />
            {commonLabels.export}
          </MuiButtonTertiary>
        </MuiBox>
      </MuiBox>

      <MuiTable
        ref={tableRef}
        pageId={pageId}
        tableHeaderForm={tableCoachReportHeaderForm}
        columns={columns}
        emptyMessage={commonLabels.searchCriteria}
        tableResults={filteredReports as unknown as Record<string, ReactNode>[]}
        noDataInApi={coachReports.length === 0}
        noResultMessage={commonLabels.noRecordsFound}
        priorityResults={[]}
        isShowDashIfValueEmpty
      />
    </MuiBox>
  );
};
